import { EditableRule, MultiSelectType } from 'src/model/EditableRule';
import { RuleTabs } from 'src/utils/enums';
import { FieldMessage, TabsErrors } from 'src/dtos/error-message.dto';
import { dnsProtectionFeature, 
    spywareProtectionFeature, 
    virusProtectionFeature, 
    vulnerabilityProtectionFeature } from 'src/components/features/protection-plus/protection-plus-config';
import { ILLEGAL_CHARACTERS_MESSAGE, ruleNameValidation, validateString } from 'src/utils/validationUtils';
import { isServiceRule } from 'src/utils/ruleUtils';
import { SecurityType } from 'src/dtos/version.dto';

class RuleValidator {
    private buildField(fieldName: string, message: string, isValid = true): FieldMessage {
        return { fieldName, message, isValid };
    }

    private validateEntitySelector(ruleData: EditableRule, displayName: string, fieldKey: string = displayName.toLowerCase(), subTitle?: string): FieldMessage {
        if (!ruleData) {
            return this.buildField(fieldKey || displayName.toLowerCase(), `${displayName} is not defined`, false);
        }

        const fieldType = `${fieldKey}Type`;
        const typeValue = (ruleData as any)[fieldType];
        const selectorValue = (ruleData as any)[fieldKey];

        if (!typeValue) {
            return this.buildField(fieldType, `${displayName} is not defined`, false);
        } else if (typeValue === MultiSelectType.ANY) {
            return this.buildField(fieldType, `${subTitle ? subTitle + ': ' : ''}Any ${displayName}`);
        } else if (selectorValue.length > 0) {
            if (displayName === 'Applications' && selectorValue.length > 200) {
                return this.buildField(fieldKey, `Limit Exceeded. You selected ${selectorValue.length} apps. Please select up to 200.`, false);
            }

            return this.buildField(fieldType, subTitle ?
                `${subTitle} is set with ${selectorValue.length} ${displayName}` :
                `${selectorValue.length} ${displayName} selected`);
        } else {
            return this.buildField(fieldType, subTitle ?
                `${subTitle} is set without ${displayName}` :
                `Selected ${displayName} without ${displayName.toLowerCase()}`, isServiceRule(ruleData.name));
        }
    }

    private getRuleNameError(ruleData: EditableRule, selectedVersionRules: any): FieldMessage {
        if (!ruleData?.name.length) {
            return this.buildField('name', 'Missing name', false);
        }

        if (ruleNameValidation(ruleData.name)) {
            return this.buildField('name', 'Invalid name', false);
        }

        let rulesToCompare = selectedVersionRules;
        if (ruleData?.id) {
            rulesToCompare = rulesToCompare.filter((_: any) => _.id !== ruleData.id);
        }

        const isDuplicated = rulesToCompare?.some((item: any) => item.displayName.toLowerCase() === ruleData.name.toLowerCase());
        if (isDuplicated) {
            return this.buildField('name', 'Duplicated name', false);
        }

        return this.buildField('name', ruleData.name);
    }

    private calculateGeneralErrors(ruleData: EditableRule, selectedVersionRules: any): FieldMessage[] {
        const generalErrors: FieldMessage[] = [
            this.getRuleNameError(ruleData, selectedVersionRules),
            ruleData?.action ?
                this.buildField('action', `Action: ${ruleData.action}`) : this.buildField('action', 'No Action Selected', false),
            ruleData?.position ?
                this.buildField('position', `Position: ${ruleData.position}`) : this.buildField('position', 'No Position Selected', false),
        ];

        if (ruleData?.description && validateString(ruleData.description) !== '') {
            generalErrors.push(this.buildField('description', ILLEGAL_CHARACTERS_MESSAGE, false));
        }

        if (ruleData?.active) {
            generalErrors.push(this.buildField('active', 'Active'));
        }

        return generalErrors;
    }

    private calculateSourceAndDestinationErrors(ruleData: EditableRule, fieldKey: 'src' | 'dst'): FieldMessage[] {
        const zoneValue = ruleData?.[`${fieldKey}Zone`];
        const errorsObj: FieldMessage[] = [
            zoneValue ?
                this.buildField(`${fieldKey}Zone`, `Zone: ${zoneValue}`) : this.buildField(`${fieldKey}Zone`, 'Zone not defined', false)
        ];

        if (zoneValue) {
            errorsObj.push(this.validateEntitySelector(ruleData, 'Address Groups', `${fieldKey}AddressGroups`, 'Traffic'),
                this.validateEntitySelector(ruleData, 'Regions', `${fieldKey}Regions`, 'Geo Location'));
        }

        return errorsObj;
    }

    private collectProfilesValues(ruleData: EditableRule): FieldMessage[] {
        return [
            this.buildField(SecurityType.AntiVirus, `Virus Protection set to ${ruleData?.antiVirus}`),
            this.buildField(SecurityType.AntiSpyware, `Spyware Protection set to ${ruleData?.antiSpyware}`),
            {
                ...this.buildField(SecurityType.DNS, `DNS Protection set to ${ruleData?.dns}`),
                componentLinkDetails: ruleData?.dns === 'Block' ? { url: 'web-filtering/domains', text: 'Manage DNS Exceptions' } : undefined
            },
            this.buildField(SecurityType.VulnerabilityProtection, `Vulnerability Protection set to ${ruleData?.vulnerabilityProtection}`),
            {
                ...this.buildField('urlFiltering', `Default Web Protection set to ${ruleData?.urlFiltering}`),
                componentLinkDetails: ruleData?.urlFiltering === 'Active' ? { url: 'web-filtering', text: 'Manage Web Protection' } : undefined
            }
        ];
    }

    private emptyRuleError(ruleData: EditableRule): FieldMessage | null {
        const emptyProfile = [dnsProtectionFeature, spywareProtectionFeature, virusProtectionFeature, vulnerabilityProtectionFeature].every((threatItem) => {
            const value = ruleData?.[threatItem.keyForGetValue as keyof EditableRule];
            return !value || value === 'Inactive';
        });
        if (ruleData?.isDefault &&
            ruleData?.srcAddressGroupsType === MultiSelectType.ANY &&
            ruleData?.srcRegionsType === MultiSelectType.ANY &&
            ruleData?.dstAddressGroupsType === MultiSelectType.ANY &&
            ruleData?.dstRegionsType === MultiSelectType.ANY &&
            ruleData?.servicesType === MultiSelectType.ANY &&
            ruleData?.applicationsType === MultiSelectType.ANY &&
            emptyProfile &&
            ruleData?.urlFiltering === 'Inactive') {
            return this.buildField('general',
                'All protections are set to ANY or Inactive. At least one protection must be specified.',
                false);
        }
        return null;
    }

    public generateErrorList(ruleData: EditableRule, selectedVersionRules: any): TabsErrors {
        if (!selectedVersionRules) return {};
        const generalTabError = this.calculateGeneralErrors(ruleData, selectedVersionRules);
        const sourceTabErrors = this.calculateSourceAndDestinationErrors(ruleData, 'src');
        const destTabErrors = this.calculateSourceAndDestinationErrors(ruleData, 'dst');
        const servicesTabErrors = [this.validateEntitySelector(ruleData, 'Services')];
        const applicationTabsErrors = [this.validateEntitySelector(ruleData, 'Applications')];
        const profileTabErrors = this.collectProfilesValues(ruleData);
        const isEmptyRuleError = this.emptyRuleError(ruleData);

        const errors: { [key: string]: FieldMessage[] } = {
            [RuleTabs.GENERAL]: generalTabError
        };
        const hasInvalidRestriction = [sourceTabErrors,
            destTabErrors,
            servicesTabErrors,
            applicationTabsErrors,
            profileTabErrors].some(errors => errors.some(error => !error.isValid));
        if (hasInvalidRestriction || !isEmptyRuleError) {
            // tabs information should be displayed when there is error in one of the tabs (except of general tab)
            // or when the rule is not empty
            errors[RuleTabs.SOURCE] = sourceTabErrors;
            errors[RuleTabs.DESTINATION] = destTabErrors;
            errors[RuleTabs.SERVICES] = servicesTabErrors;
            errors[RuleTabs.APPLICATIONS] = applicationTabsErrors;
            errors[RuleTabs.PROFILES] = profileTabErrors;
        } else if (isEmptyRuleError) {
            errors['Source, Destination, Services, Applications, Profiles'] = [isEmptyRuleError];
        }
        return errors;
    }
}
const ruleValidator = new RuleValidator();
export default ruleValidator;